/* The quick bar — the chrome that returns. The bar itself is display:none on desktop; on a phone
   it slides down once the real chrome's bottom edge is above the viewport, and back up when the
   visitor returns to the top. It drives the SAME details element the chrome's Menu summary opens
   (one menu, two triggers, so the panel's scroll lock and the reference's own arrangement are
   untouched). */
(() => {
  const bar = document.querySelector('[data-quick]');
  if (!bar) return;
  const root = document.documentElement;
  const chrome = document.querySelector('.v2-chrome');
  const menu = document.querySelector('.v2-menu');

  if (chrome) {
    /* A rect test on scroll, not an IntersectionObserver. The observer computes exactly this and
       is tidier, but it does not fire at all in some preview environments (a hidden pane, an
       occluded iframe), so its behaviour cannot be observed there — and this is observable
       everywhere: one read per scroll event, which is already per-frame, plus one at load and
       one on resize.

       ⚠️ On the homepage the deck (lv-hero.js) pins the chrome where it stands: it never leaves
       the viewport rect, the next screen COVERS it instead. So there the bar's question is not
       "has the chrome left?" but "is the chrome covered?" — read as the screen after the hero
       having risen past the chrome's own top edge. Every other page keeps the plain exit test. */
    const hero = document.querySelector('[data-hero]');
    const next = hero?.closest('.shopify-section')?.nextElementSibling ?? null;
    const check = () => {
      const bottom = chrome.getBoundingClientRect().bottom;
      const covered =
        next && root.classList.contains('v2-deck')
          ? next.getBoundingClientRect().top <= Math.max(0, chrome.getBoundingClientRect().top)
          : false;
      root.classList.toggle('v2-quick-on', bottom <= 0 || covered);
    };
    addEventListener('scroll', check, { passive: true });
    addEventListener('resize', check, { passive: true });
    check();
  }

  const trigger = bar.querySelector('[data-quick-menu]');
  if (trigger && menu) {
    const sync = () => trigger.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
    trigger.addEventListener('click', () => {
      if (menu.open) closeMenu();
      else openMenu();
    });
    document.addEventListener(
      'toggle',
      (event) => {
        if (event.target === menu) sync();
      },
      true,
    );
    sync();
  }

  /* Escape closes the panel and puts focus back on the control that opened it. Without this the
     panel has an exit only on screen: its Close summary lives at the top of the page, and opening
     the menu locks body scroll — so a keyboard user who opens it from further down (or from the
     quick bar) would have nothing on screen to close it with. */
  /* The drawer's close is choreographed: the details stays open — so the page stays locked and
     the panel stays rendered — while .v2-menu--leaving slides it out, and the attribute flips
     when the slide is done. A details hides its content the instant it closes, which is why a
     plain menu.open = false made the drawer teleport instead of leave. */
  let leaving = 0;
  const closeMenu = () => {
    if (!menu?.open) return;
    menu.classList.add('v2-menu--leaving');
    clearTimeout(leaving);
    leaving = setTimeout(() => {
      menu.classList.remove('v2-menu--leaving');
      menu.open = false;
    }, 300);
  };
  const openMenu = () => {
    if (!menu) return;
    clearTimeout(leaving);
    menu.classList.remove('v2-menu--leaving');
    menu.open = true;
  };
  const summary = menu?.querySelector('summary');
  if (summary) {
    summary.addEventListener('click', (event) => {
      event.preventDefault(); // the toggle is ours now; without this the close loses its beat
      if (menu.open) closeMenu();
      else openMenu();
    });
  }

  if (menu) {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !menu.open) return;
      closeMenu();
      (trigger || menu.querySelector('summary'))?.focus();
    });
    /* The drawer covers the chrome while it is open, so it carries its own close control —
       the summary up in the chrome is under the panel then. */
    for (const el of menu.querySelectorAll('[data-menu-close]')) {
      el.addEventListener('click', () => {
        closeMenu();
        (trigger || menu.querySelector('summary'))?.focus();
      });
    }
    /* A drawer yields to a click on the page it covers. Its own summary and panel are the menu;
       the quick bar's button is the menu's second opener and toggles for itself. */
    document.addEventListener('click', (event) => {
      if (!menu.open) return;
      if (event.target.closest?.('.v2-menu, [data-quick-menu]')) return;
      closeMenu();
    });
  }
})();
